@if (isConfigured()) {
<div class="card mb-4">
  <div class="card-header d-flex justify-content-between align-items-center p-3">
    <h5 class="mb-0"><i class="bi bi-leaf me-2"></i>Agremo Maps</h5>
    <button
      type="button"
      class="btn btn-sm"
      [class.btn-outline-danger]="showUploadForm()"
      [class.btn-outline-primary]="!showUploadForm()"
      (click)="toggleUploadForm()"
    >
      <i class="bi" [class.bi-x-lg]="showUploadForm()" [class.bi-plus-lg]="!showUploadForm()"></i>
      {{ showUploadForm() ? 'Cancel' : 'Upload Map' }}
    </button>
  </div>

  <div class="card-body">
    <!-- Upload Form -->
    @if (showUploadForm()) {
      <div class="upload-section mb-3 p-3 border rounded">
        <!-- Drag & Drop Zone -->
        @if (!uploadedFile()) {
          <div
            class="drop-zone text-center p-4 border border-2 border-dashed rounded mb-3"
            [class.drag-over]="isDragOver()"
            (dragover)="onDragOver($event)"
            (dragleave)="onDragLeave($event)"
            (drop)="onDrop($event)"
          >
            <i class="bi bi-cloud-arrow-up fs-2 text-muted"></i>
            <p class="mb-1 text-muted">Drag & drop GeoTIFF imagery here</p>
            <p class="small mb-2 text-muted">TIFF, JPEG, PNG, or ZIP (max 500MB)</p>
            <label class="btn btn-sm btn-outline-secondary">
              Browse Files
              <input type="file" class="d-none" accept=".tif,.tiff,.jpg,.jpeg,.png,.zip" (change)="onFileSelect($event)">
            </label>
            <p class="small mb-0 mt-2 text-muted">
              Upload the original export from your mapping software. Files downloaded or previewed through
              messaging apps, photo apps, or email often strip the location data GeoTIFFs need.
            </p>
          </div>
        } @else {
          <!-- File Selected -->
          <div class="file-selected d-flex align-items-center justify-content-between mb-3 p-2 border rounded">
            <div>
              <i class="bi bi-file-earmark-image me-2"></i>
              <strong>{{ uploadedFile()!.name }}</strong>
              <span class="ms-2 text-muted">({{ formatFileSize(uploadedFile()!.size) }})</span>
            </div>
            <button type="button" class="btn btn-sm btn-outline-danger" (click)="removeFile()" [disabled]="isUploading()">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
        }

        <!-- Map Metadata -->
        <div class="row g-2 mb-3">
          <div class="col-md-5">
            <label class="form-label small">Map Name</label>
            <input type="text" class="form-control form-control-sm" [(ngModel)]="mapName" placeholder="e.g., Spring Survey">
          </div>
          <div class="col-md-4">
            <label class="form-label small">Map Date</label>
            <input type="date" class="form-control form-control-sm" [(ngModel)]="mapDate">
          </div>
          <div class="col-md-3">
            <label class="form-label small">Camera Type</label>
            <select class="form-select form-select-sm" [(ngModel)]="cameraType">
              <option value="RGB">RGB</option>
              <option value="Multispectral">Multispectral</option>
              <option value="Thermal">Thermal</option>
            </select>
          </div>
        </div>

        <!-- Error -->
        @if (uploadError()) {
          <div class="alert alert-danger py-2 small mb-3">{{ uploadError() }}</div>
        }

        <!-- Submit -->
        <div class="text-end">
          <button
            type="button"
            class="btn btn-sm btn-primary"
            [disabled]="!canSubmit"
            (click)="submitUpload()"
          >
            @if (isUploading()) {
              <span class="spinner-border spinner-border-sm me-1"></span> Uploading...
            } @else {
              <i class="bi bi-cloud-arrow-up me-1"></i> Upload & Process
            }
          </button>
        </div>
      </div>
    }

    <!-- Maps Table -->
    @if (isLoading()) {
      <div class="text-center py-3">
        <span class="spinner-border spinner-border-sm"></span>
        <span class="ms-2 text-muted">Loading maps...</span>
      </div>
    } @else if (maps().length === 0) {
      <p class="text-muted small mb-0">No maps uploaded yet. Upload a GeoTIFF to get started.</p>
    } @else {
      <div class="table-responsive">
        <table class="table table-sm table-hover mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th>Date</th>
              <th>Camera</th>
              <th>Status</th>
              <th>Analyses</th>
              <th class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            @for (map of maps(); track map.id) {
              <tr>
                <td>{{ map.mapName }}</td>
                <td>{{ map.mapDate | date:'mediumDate' }}</td>
                <td>{{ map.cameraType }}</td>
                <td>
                  <span class="badge" [ngClass]="getStatusBadgeClass(map.status)">
                    @if (isProcessing(map.status)) {
                      <span class="spinner-border spinner-border-sm me-1 map-status-spinner"></span>
                    }
                    {{ getStatusText(map.status) }}
                  </span>
                  @if (map.status === 'failed' && map.errorMessage) {
                    <div class="text-danger small mt-1 map-error">{{ map.errorMessage }}</div>
                  }
                </td>
                <td>
                  @if (map.analysisCount > 0) {
                    <a [routerLink]="['/analyses']" [queryParams]="{ mapId: map.id }">{{ map.analysisCount }}</a>
                  } @else {
                    0
                  }
                </td>
                <td class="text-end">
                  @if (map.status === 'failed') {
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-warning me-1"
                      title="Recheck map status in Agremo"
                      [disabled]="recheckingMapId() !== null"
                      (click)="recheckMap(map)"
                    >
                      @if (recheckingMapId() === map.id) {
                        <span class="spinner-border spinner-border-sm"></span>
                      } @else {
                        <i class="bi bi-arrow-repeat"></i>
                      }
                    </button>
                  }
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    title="Delete map"
                    (click)="confirmDeleteMap(map)"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </div>
</div>

<!-- Delete Confirmation Modal -->
<app-confirm-modal
  #confirmModal
  (confirmed)="onDeleteConfirmed()"
></app-confirm-modal>
}
